
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { GotoBlocklyDirective } from 'src/app/shared/directives/goto-blockly.directive';
import { PlaceholderInfoDirective } from 'src/app/shared/directives/placeholder-info.directive';
import { SubscriptionLike } from 'rxjs';
import { StageAdminService } from '@shared-angular/services/stage-admin.service';
import { CodemirrorHtmlComponent } from "../../../../../../../../shared/components/ai-bot/utils/codemirror-html/codemirror-html.component";
import { FusionService } from '@shared-angular/services/fusion.service';
import { getFusionBotTypeOptions } from '../fusionbot-tab.utils';
import { LanguageService } from '@shared-angular/services/language.service';
import { RouteShareService } from '../../../../../../../../shared/services/route-share.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { EditorItemsService } from 'src/app/shared/services/editor-items.service';

@Component({
  selector: 'app-fusion-html-as-media-config',
  imports: [FormsModule, CodemirrorHtmlComponent, GotoBlocklyDirective, PlaceholderInfoDirective, ButtonModule, InputTextModule, SelectModule, ToggleSwitchModule],
  templateUrl: './fusion-html-as-media-config.component.html',
  styleUrl: './fusion-html-as-media-config.component.scss'
})
export class FusionHtmlAsMediaConfigComponent implements OnInit, OnDestroy {
  public safeHtml: SafeHtml = "";
  public subscriptions: SubscriptionLike[] = [];
  public showUrlInput = false;
  protected get botTypeOptions() {
    return getFusionBotTypeOptions(this.fusionService.loginService.systemConf);
  }

  protected systemLanguageOptions = [
    { label: $localize`:@@fusionconfig-systemlang-de:Deutsch`, value: 'de' },
    { label: $localize`:@@fusionconfig-systemlang-en:Englisch`, value: 'en' },
    { label: $localize`:@@fusionconfig-systemlang-fr:Französisch`, value: 'fr' },
    { label: $localize`:@@fusionconfig-systemlang-es:Spanisch`, value: 'es' },
    { label: $localize`:@@fusionconfig-systemlang-it:Italienisch`, value: 'it' },
    { label: $localize`:@@fusionconfig-systemlang-nl:Niederländisch`, value: 'nl' },
    { label: $localize`:@@fusionconfig-systemlang-nb:Norwegisch`, value: 'nb-no' },
    { label: $localize`:@@fusionconfig-systemlang-fr-be:Französisch (Belgien)`, value: 'fr-be' },
    { label: $localize`:@@fusionconfig-systemlang-nl-be:Niederländisch (Belgien)`, value: 'nl-be' },
    { label: $localize`:@@fusionconfig-systemlang-pl:Polnisch`, value: 'pl' },
    { label: $localize`:@@fusionconfig-systemlang-pt:Portugiesisch`, value: 'pt' },
    { label: $localize`:@@fusionconfig-systemlang-hu:Ungarisch`, value: 'hu' },
    { label: $localize`:@@fusionconfig-systemlang-ro:Rumänisch`, value: 'ro' },
    { label: $localize`:@@fusionconfig-systemlang-tr:Türkisch`, value: 'tr' },
    { label: $localize`:@@fusionconfig-systemlang-fi:Finnisch`, value: 'fi' },
    { label: $localize`:@@fusionconfig-systemlang-sv:Schwedisch`, value: 'sv' },
    { label: $localize`:@@fusionconfig-systemlang-da:Dänisch`, value: 'da' },
    { label: $localize`:@@fusionconfig-systemlang-cs:Tschechisch`, value: 'cs' },
    { label: $localize`:@@fusionconfig-systemlang-el:Griechisch`, value: 'el' }
  ];

  protected viewModeOptions = [
    { label: 'Split', value: 'split' },
    { label: 'Split Reverse', value: 'split-reverse' },
    { label: 'Window', value: 'window' },
    { label: 'Full', value: 'full' },
    { label: 'Float', value: 'float' }
  ];

  constructor(public fusionService: FusionService,
    public rss: RouteShareService,
    public langService: LanguageService,
    public sanitizer: DomSanitizer,
    public toastr: ToastrService,
    protected stageAdmin: StageAdminService,
    public editorItemService: EditorItemsService
  ) { }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => { s.unsubscribe(); });
  }

  public ngOnInit(): void {
    this.subscriptions.push(this.fusionService.onFusionConfigChanged.subscribe(() => {
      this.updateSafeUrlString();
    }));

    this.subscriptions.push(this.fusionService.onFusionConfigDeleted.subscribe(() => {
      this.updateSafeUrlString();
    }));

    this.subscriptions.push(this.fusionService.onFusionConfigCreated.subscribe(() => {
      this.updateSafeUrlString();
    }));

    this.updateSafeUrlString();
  }

  public updateSafeUrlString() {
    if (this.fusionService.selectedHtmlPage) {
      this.safeHtml = this.sanitizer.bypassSecurityTrustResourceUrl(this.fusionService.selectedHtmlPage.metadata.proxyUrl);
    }
    return "";
  }

  public async loadURL(url: string) {
    const result = await this.fusionService.embedInfo(url);
    if (result.status != "ok") {
      this.toastr.error("ERROR_400", ($localize`:@@invalid-domain:URL ist ungültig`));
      return;
    }

    const html = await this.fusionService.getHtml(url);
    await this.fusionService.updateFusionField('proxyUrl', url);
    await this.fusionService.updateFusionField('useStaticHtml', true);
    await this.fusionService.updateFusionField('staticHtml', html);
  }

  public async addURL(url: string) {
    const result = await this.fusionService.embedInfo(url);
    if (result.status != "ok") {
      this.toastr.error("ERROR_400", ($localize`:@@invalid-domain:URL ist ungültig`));
      return;
    }

    await this.fusionService.updateFusionField('proxyUrl', url);
    await this.fusionService.updateFusionField('useStaticHtml', false);
  }

  public async resetMode() {
    await this.fusionService.updateFusionField('useStaticHtml', false);
    await this.fusionService.updateFusionField('proxyUrl', '');
    this.showUrlInput = false;
  }

}
